<script setup lang="ts">
import { onMounted, ref, shallowRef } from 'vue'
import { MessageApprovalApiError } from '@/@vxapp/Messages/Approvals/messageApprovalApi'
import { messageApprovalSubjects } from '@/@vxapp/Messages/Approvals/messageApprovalTypes'
import type {
  MessageApprovalFailure,
  MessageApprovalView,
} from '@/@vxapp/Messages/Approvals/messageApprovalTypes'
import { useMessageApprovalStore } from '@/@vxapp/Messages/Approvals/useMessageApprovalStore'
import MessageApprovalDialog from '@/components/Dialogs/Messages/MessageApprovalDialog.vue'
import {
  approvalFailureText,
  approvalTime,
  serviceSummary,
} from '@/components/Dialogs/Messages/messageApprovalPresentation'

// Assistant-prepared emails waiting for a person's approval, newest first. Nothing on this page
// sends an email: a row opens the review dialog, which decides only on explicit confirmation.

definePage({
  meta: { subject: messageApprovalSubjects.list, layout: null, pageTitle: 'Email approvals' },
})

const PAGE_LIMIT = 20
const store = useMessageApprovalStore()
const approvals = ref<MessageApprovalView[]>([])
const nextPage = shallowRef<number | null>(null)
const loading = shallowRef(false)
const loaded = shallowRef(false)
const failure = shallowRef<MessageApprovalFailure | null>(null)
const selectedGuid = shallowRef<string | null>(null)
const dialogOpen = shallowRef(false)

const recipientCount = (view: MessageApprovalView) =>
  `${view.recipients.length} ${view.recipients.length === 1 ? 'recipient' : 'recipients'}`

async function loadPage(page: number) {
  loading.value = true
  failure.value = null
  try {
    const result = await store.listPage(page, PAGE_LIMIT)
    const known = new Set(page === 1 ? [] : approvals.value.map(view => view.messageGUID))

    approvals.value = [
      ...(page === 1 ? [] : approvals.value),
      ...result.approvals.filter(view => !known.has(view.messageGUID)),
    ]
    nextPage.value = result.nextPage
    loaded.value = true
  } catch (error) {
    if (page === 1) {
      approvals.value = []
      nextPage.value = null
    }
    failure.value =
      error instanceof MessageApprovalApiError
        ? error.failure
        : {
            code: 'REQUEST_FAILED' as MessageApprovalFailure['code'],
            message: '',
            retryAfterSeconds: null,
            outcome: 'not_applied',
          }
  } finally {
    loading.value = false
  }
}
function refresh() {
  void loadPage(1)
}
function loadMore() {
  if (nextPage.value && !loading.value) void loadPage(nextPage.value)
}
function openApproval(view: MessageApprovalView) {
  selectedGuid.value = view.messageGUID
  dialogOpen.value = true
}
onMounted(refresh)
</script>

<template>
  <section class="message-approvals">
    <header class="message-approvals__header">
      <div>
        <h1>Email approvals</h1>
        <p>
          Emails the assistant prepared for quotes and invoices. Nothing is sent until someone
          approves it here.
        </p>
      </div>
      <VBtn
        :disabled="loading"
        @click="refresh"
        >Refresh</VBtn
      >
    </header>
    <p
      v-if="failure"
      role="alert"
      class="message-approvals__alert"
    >
      {{ approvalFailureText(failure) }}
    </p>
    <p
      v-else-if="loaded && !approvals.length"
      class="message-approvals__empty"
    >
      No emails are waiting for approval
    </p>
    <p
      v-else-if="!loaded"
      role="status"
    >
      Loading emails…
    </p>
    <ul
      v-if="approvals.length"
      class="message-approvals__list"
    >
      <li
        v-for="view in approvals"
        :key="view.messageGUID"
      >
        <button
          type="button"
          class="message-approvals__row"
          data-testid="approval-row"
          @click="() => openApproval(view)"
        >
          <strong>{{ view.subject || '(no subject)' }}</strong>
          <span>{{ serviceSummary(view) }} · {{ recipientCount(view) }}</span>
          <span>Requested {{ approvalTime(view.requestedAt) }}</span>
          <VChip
            v-if="view.replyTo"
            size="small"
            >Reply</VChip
          >
        </button>
      </li>
    </ul>
    <VBtn
      v-if="nextPage"
      variant="tonal"
      :disabled="loading"
      @click="loadMore"
      >Load more</VBtn
    >
    <MessageApprovalDialog
      v-if="selectedGuid"
      v-model="dialogOpen"
      :message-guid="selectedGuid"
      @decided="refresh"
    />
  </section>
</template>

<style scoped>
.message-approvals {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.message-approvals__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.message-approvals__header h1 {
  margin: 0 0 4px;
  font-size: 1.5rem;
  font-weight: 600;
}
.message-approvals__header p,
.message-approvals__empty,
.message-approvals__alert {
  margin: 0;
}
.message-approvals__alert {
  color: rgb(var(--v-theme-error));
}
.message-approvals__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  margin: 0;
  list-style: none;
}
.message-approvals__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  inline-size: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  color: inherit;
  text-align: start;
  cursor: pointer;
}
.message-approvals__row strong {
  flex: 1 1 100%;
  overflow-wrap: anywhere;
}
.message-approvals__row:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
</style>
